import { create } from "zustand";
import { combine } from "zustand/middleware";
import { isEqual } from "lodash";
import {
  getCountRequestKey,
  loadItems,
  updateFilterPosition,
  type Cursor,
  type LoadItemsOptions,
  type TimeCursor,
} from "@/lib/client/api";
import { defaultFilters } from "@/lib/shared/constants";
import { logError } from "@/lib/client/errors";
import { useFiltersStore } from "@/stores/filtersStore";
import { generateUUID } from "@/lib/client/generateUUID";

/**
 * Default total item count and price range data.
 * Used as cached value when filters are at default state.
 */
type DefaultTotal = {
  total: number;
  minPrice: number;
  maxPrice: number;
  avgPrice?: number;
};

/**
 * Checks if the current filters are at their default state.
 * Used to determine if cached totals can be used.
 */
const isDefaultFilters = (
  activeFilters: ActiveFilters,
  searchText: string,
  savedFilterId?: number
) => {
  if (searchText || savedFilterId) {
    return false;
  }
  return isEqual(activeFilters, defaultFilters);
};

/**
 * Extracts filters excluding price.
 * Allows price range sliders to update without refetching total count.
 */
const filtersExcludingPrice = (filters: ActiveFilters) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { price, ...rest } = filters;
  return rest;
};

const hasKeywordSearch = (searchText: string) => searchText.trim().length > 0;

/**
 * Exponential backoff with jitter: 1.5s, 3s, 6s, 12s, 24s, capped at 30s.
 * Generous enough to let a rate-limit window reset, short enough that the
 * feed recovers without user action.
 */
const retryBackoffMs = (failureCount: number) =>
  Math.min(1500 * 2 ** Math.max(0, failureCount - 1), 30000) + Math.random() * 500;

/**
 * After this many consecutive failures the automatic retry pulse stops
 * (shouldRetry goes false) so an idle tab doesn't poll a dead backend
 * forever. User scrolling still attempts loads, paced by nextRetryAt.
 */
const MAX_AUTO_RETRIES = 8;

const hasScrolledSearchContainer = () => {
  const windowScrollTop = typeof window === "undefined" ? 0 : window.scrollY;
  const searchScrollTop =
    typeof document === "undefined" ? 0 : (document.getElementById("masonary")?.scrollTop ?? 0);

  return Math.max(windowScrollTop, searchScrollTop) > 0;
};

const hasReliableKeywordTotal = (total: number | undefined, itemsLength: number) =>
  typeof total === "number" && (total > 0 || itemsLength === 0);

const resolveKeywordSearchTotal = ({
  responseTotal,
  fallbackTotal,
  itemsLength,
}: {
  responseTotal?: number;
  fallbackTotal?: number;
  itemsLength: number;
}) => {
  if (hasReliableKeywordTotal(responseTotal, itemsLength)) {
    return responseTotal;
  }

  if (hasReliableKeywordTotal(fallbackTotal, itemsLength)) {
    return fallbackTotal;
  }

  return responseTotal;
};

const resolveHasMore = ({
  itemsLength,
  limit,
  nextCursor,
  searchText,
  loadedCount,
  total,
}: {
  itemsLength: number;
  limit: number;
  nextCursor?: Cursor | null;
  searchText: string;
  loadedCount: number;
  total?: number;
}) => {
  if (!itemsLength) {
    return false;
  }

  if (hasKeywordSearch(searchText)) {
    if (typeof total === "number") {
      return loadedCount < total;
    }

    return itemsLength >= limit;
  }

  if (nextCursor !== undefined) {
    return nextCursor !== null;
  }

  return itemsLength >= limit;
};

/**
 * Initial state for the items store.
 * Contains search results, pagination state, loading states, and metadata.
 */
const initialState = {
  /** Current search results */
  items: [] as SearchResultItem[],
  /** Total number of items matching search (from API) */
  itemTotal: 0,
  /** Minimum price across all items */
  itemMinPrice: 0,
  /** Maximum price across all items */
  itemMaxPrice: Infinity,
  /** Average price across all items */
  itemAvgPrice: undefined as number | undefined,
  /** Current offset for pagination (number of items loaded) */
  itemOffset: 0,
  /** Cursor for next page (id or {price, id}) */
  nextCursor: null as Cursor | null,
  /** Main loading state for search operations */
  loading: false,
  /** Loading state for total count/price range queries */
  totalLoading: false,
  /** Last filters used for total query (for caching) */
  lastFiltersForTotal: null as ActiveFilters | null,
  /** Last search text used for total query (for caching) */
  lastSearchTextForTotal: "" as string,
  /** Whether the search returned no results */
  noResults: false,
  /** Whether all items have been loaded (no more to fetch) */
  hasReachedEnd: false,
  /** Whether more items are available to load */
  loadMore: false,
  /** Timestamp of last load operation */
  lastLoadTime: 0,
  /** Number of consecutive failed load attempts */
  retryCount: 0,
  /** Whether a retry should be attempted */
  shouldRetry: false,
  /**
   * Earliest timestamp (ms) at which the next load-more attempt is allowed.
   * Set with exponential backoff after failures so retry pulses from the
   * scroll trigger can't hammer a rate-limited API.
   */
  nextRetryAt: 0,
  /** Number of times items have been loaded via scroll */
  scrollLoadCount: 0,
  /** Whether to show the sign-in prompt (triggered at load counts 5, 15, 25...) */
  showSignInPrompt: false,
  /** Count of listings added in the last 24 hours matching current filters (shown when KURBO is off) */
  recentItemCount: 0,
  /** If set, the current search is a "new results" mode filtered to items newer than this timestamp */
  activeNewFrom: null as string | null,
  currentJobId: null as string | null,
  /**
   * Tracks which saved filter IDs have been scrolled in this session.
   * Unlike scrollLoadCount, this is NOT reset by loadInitialResults — it persists across
   * filter switches so buttons stay greyed after you've scrolled a filter and return to it.
   * Only cleared by a full reset().
   */
  sessionScrolledFilterIds: {} as Record<number, true>,
};

/**
 * Items store manages search results and pagination.
 *
 * Features:
 * - Initial search with items and totals
 * - Infinite scroll pagination with load more
 * - Price range caching (skips refetch when only price changes)
 * - Sign-in prompt at scroll milestones (5, 15, 25...)
 * - Retry logic for failed requests
 *
 * @example
 * ```ts
 * const { actions } = useItemsStore();
 * await actions.loadInitialResults({ vendors, brands, exchangeRates, activeFilters, searchText });
 * await actions.loadMoreItems({ vendors, brands, exchangeRates, activeFilters, searchText });
 * ```
 */
export const useItemsStore = create(
  combine(initialState, (set, get) => {
    let latestCountRequestId = 0;
    let loadMoreRequestInFlight = false;
    let lastResolvedCountKey: string | null = null;

    const actions = {
      /**
       * Loads initial search results with items and totals.
       * Resets state, scrolls to top, and fetches first page + total count.
       * Uses cached defaultTotal if filters are at default state.
       *
       * @param params - Search parameters including filters, vendors, brands, exchange rates
       */
      loadInitialResults: async (params: {
        vendors: VendorFilter[];
        brands: BrandFilter[];
        exchangeRates: Currency;
        activeFilters: ActiveFilters;
        searchText: string;
        savedFilterId?: number;
        signal?: AbortSignal;
        defaultTotal?: DefaultTotal;
        newFrom?: string;
        resumeFrom?: string;
        skipTotalOnly?: boolean;
      }) => {
        const {
          vendors,
          brands,
          exchangeRates,
          activeFilters,
          searchText,
          savedFilterId,
          signal,
          newFrom,
          resumeFrom,
          defaultTotal,
          skipTotalOnly,
        } = params;
        set({
          items: [],
          noResults: false,
          hasReachedEnd: false,
          lastLoadTime: 0,
          retryCount: 0,
          shouldRetry: false,
          nextRetryAt: 0,
          scrollLoadCount: 0,
          nextCursor: null,
          activeNewFrom: newFrom ?? null,
          currentJobId: null,
        });

        if (!vendors?.length || !brands?.length) {
          return;
        }

        const shouldUseDefaultTotal =
          !!defaultTotal && isDefaultFilters(activeFilters, searchText, savedFilterId);

        // Immediately apply cached default total so the count shows without a duplicate stats request.
        if (shouldUseDefaultTotal) {
          set({
            itemTotal: defaultTotal.total,
            itemMinPrice: defaultTotal.minPrice ?? 0,
            itemMaxPrice: defaultTotal.maxPrice ?? Infinity,
            itemAvgPrice: defaultTotal.avgPrice,
            lastFiltersForTotal: activeFilters,
            lastSearchTextForTotal: searchText,
          });
        }

        const limit = 30;
        const countOptions: LoadItemsOptions = {
          query: activeFilters,
          searchText,
          limit: 1,
          offset: 0,
          exchangeRates,
          filterid: savedFilterId,
          signal,
          countOnly: true,
          resumeFrom,
          newFrom,
        };
        const countRequestKey = getCountRequestKey(countOptions);
        const shouldLoadStats = !skipTotalOnly && !shouldUseDefaultTotal;
        const shouldLoadCount = !skipTotalOnly && countRequestKey !== lastResolvedCountKey;

        try {
          set({ loadMore: true, loading: true, totalLoading: shouldLoadCount });

          document.getElementById("masonary")?.scrollTo(0, 0);

          const statsResultPromise: Promise<Awaited<ReturnType<typeof loadItems>> | undefined> =
            shouldLoadStats
              ? loadItems({
                  query: activeFilters,
                  searchText,
                  limit: 1,
                  offset: 0,
                  exchangeRates,
                  filterid: savedFilterId,
                  signal,
                  totalOnly: true,
                }).catch((error) => {
                  if (!signal?.aborted) {
                    logError(error, {
                      action: "loadInitialResults.stats",
                    });
                  }
                  return undefined;
                })
              : Promise.resolve(undefined);

          void statsResultPromise.then((statsResult) => {
            if (!signal?.aborted && statsResult) {
              set({
                // Fast sampled total is only a placeholder until this filter's
                // exact count resolves. Later stats refreshes must not replace it.
                ...(lastResolvedCountKey === countRequestKey
                  ? {}
                  : { itemTotal: statsResult.total ?? 0 }),
                itemMinPrice: statsResult.minPrice ?? 0,
                itemMaxPrice: statsResult.maxPrice ?? Infinity,
                itemAvgPrice: statsResult.avgPrice,
                lastFiltersForTotal: activeFilters,
                lastSearchTextForTotal: searchText,
              });
            }
          });

          const itemsResultPromise = loadItems({
            query: activeFilters,
            searchText,
            limit,
            offset: 0,
            exchangeRates,
            filterid: savedFilterId,
            signal,
            newFrom,
            resumeFrom,
          });

          const countRequestId = ++latestCountRequestId;
          const countResultPromise: Promise<Awaited<ReturnType<typeof loadItems>> | undefined> =
            shouldLoadCount
              ? Promise.resolve(loadItems(countOptions)).catch((error) => {
                  if (!signal?.aborted) {
                    logError(error, {
                      action: "loadInitialResults.count",
                    });
                  }
                  return undefined;
                })
              : Promise.resolve(undefined);

          void countResultPromise.then((countResult) => {
            if (!signal?.aborted && countRequestId === latestCountRequestId && countResult) {
              lastResolvedCountKey = countRequestKey;
              set({
                itemTotal: countResult.total ?? 0,
                lastFiltersForTotal: activeFilters,
                lastSearchTextForTotal: searchText,
              });
            }
            if (!signal?.aborted && countRequestId === latestCountRequestId && shouldLoadCount) {
              set({ totalLoading: false });
            }
          });

          const itemsResult = await itemsResultPromise;

          if (!itemsResult?.items || !Array.isArray(itemsResult.items)) {
            // Failed first page (not an abort): arm the shared retry pulse.
            // loadMore was set true above, so the retried loadMoreItems call
            // appends onto the empty list — equivalent to loading page 1.
            // Without this, a 429 on the initial load strands the user on
            // ghost cards with no recovery.
            if (!signal?.aborted) {
              const failureCount = get().retryCount + 1;
              set({
                retryCount: failureCount,
                shouldRetry: failureCount < MAX_AUTO_RETRIES,
                nextRetryAt: Date.now() + retryBackoffMs(failureCount),
              });
            }
            return;
          }

          const hasMore = resolveHasMore({
            itemsLength: itemsResult.items.length,
            limit,
            nextCursor: itemsResult.nextCursor,
            searchText,
            loadedCount: itemsResult.items.length,
            total:
              hasKeywordSearch(searchText) &&
              !hasReliableKeywordTotal(itemsResult.total, itemsResult.items.length)
                ? undefined
                : itemsResult.total,
          });

          if (!signal?.aborted) {
            const noResults = !itemsResult.items.length;
            set({
              items: itemsResult.items,
              itemOffset: itemsResult.items.length,
              nextCursor: itemsResult.nextCursor ?? null,
              loadMore: noResults ? false : hasMore,
              hasReachedEnd: noResults ? true : !hasMore,
              noResults,
              loading: false,
              recentItemCount: itemsResult.recentItemCount ?? 0,
            });
          }
        } finally {
          if (!signal?.aborted) {
            // Safety net for error paths — idempotent on success.
            set({ loading: false });
          }
        }
      },

      /**
       * Loads more items for infinite scroll pagination.
       * Shows sign-in prompt at milestones and preserves pagination state.
       * Handles errors with retry logic.
       *
       * @param params - Search parameters matching the current search
       */
      loadMoreItems: async (params: {
        vendors: VendorFilter[];
        brands: BrandFilter[];
        exchangeRates: Currency;
        activeFilters: ActiveFilters;
        searchText: string;
        isSignedIn?: boolean;
        savedFilterId?: number;
      }) => {
        // Claim pagination synchronously. Multiple observers/callbacks can fire
        // before React paints the store's loading state, but only one of them
        // may own the next page request.
        if (loadMoreRequestInFlight) {
          return false;
        }

        const {
          vendors,
          brands,
          exchangeRates,
          activeFilters,
          searchText,
          isSignedIn,
          savedFilterId,
        } = params;
        const {
          itemOffset,
          nextCursor,
          hasReachedEnd,
          loading,
          loadMore,
          items,
          scrollLoadCount,
          shouldRetry,
          nextRetryAt,
          currentJobId: storedJobId,
        } = get();

        if (!vendors?.length || !brands?.length || hasReachedEnd || loading) {
          return false;
        }

        // Backoff gate: after a failure, refuse attempts until the window
        // elapses. The scroll trigger re-pulses periodically; this is the
        // single place that decides whether a request may actually go out.
        if (Date.now() < nextRetryAt) {
          return false;
        }

        loadMoreRequestInFlight = true;
        set({ loading: true, lastLoadTime: Date.now() });

        if (loadMore) {
          const jobId = shouldRetry && storedJobId ? storedJobId : generateUUID();
          set({ currentJobId: jobId });

          try {
            // Matches the initial-load page size. Larger pages mean fewer
            // requests per scroll session — less 429 exposure on deep scrolls.
            const limit = 30;
            // Use offset when searching (BM25 ranks are dynamic), cursor otherwise
            const isKeywordSearch = hasKeywordSearch(searchText);

            const result = await loadItems({
              query: activeFilters,
              searchText,
              limit,
              offset: isKeywordSearch ? itemOffset : undefined,
              cursor: isKeywordSearch ? undefined : (nextCursor ?? undefined),
              exchangeRates,
              filterid: savedFilterId,
              jobId,
            });

            if (!result?.items || !Array.isArray(result.items)) {
              const failureCount = get().retryCount + 1;
              set({
                retryCount: failureCount,
                shouldRetry: failureCount < MAX_AUTO_RETRIES,
                nextRetryAt: Date.now() + retryBackoffMs(failureCount),
                loading: false,
              });
              return false;
            }

            const totalCount = isKeywordSearch
              ? resolveKeywordSearchTotal({
                  responseTotal: result.total,
                  fallbackTotal: get().itemTotal,
                  itemsLength: result.items.length,
                })
              : result.total;
            const loadedCount = items.length + result.items.length;

            const hasMoreResults = resolveHasMore({
              itemsLength: result.items.length,
              limit,
              nextCursor: result.nextCursor,
              searchText,
              loadedCount,
              total: totalCount,
            });

            set({
              items: [...items, ...result.items],
              itemOffset: loadedCount,
              nextCursor: result?.nextCursor ?? null,
              retryCount: 0,
              shouldRetry: false,
              nextRetryAt: 0,
              loadMore: hasMoreResults,
              hasReachedEnd: !hasMoreResults,
              scrollLoadCount: scrollLoadCount + 1,
              // Sign-in nag at loads 5, 15, 25, etc. Set only on success so
              // failed attempts and their retries can't reopen a modal the
              // user already dismissed. A nag, not a gate: the items above
              // load regardless.
              ...(!isSignedIn && (scrollLoadCount + 1) % 10 === 5
                ? { showSignInPrompt: true }
                : {}),
              ...(savedFilterId
                ? {
                    sessionScrolledFilterIds: {
                      ...get().sessionScrolledFilterIds,
                      [savedFilterId]: true,
                    },
                  }
                : {}),
            });

            // Update resume position explicitly — only when the user has genuinely
            // scrolled the search results container and only for time-sorted results
            // (TimeCursor has a timestamp field).
            if (
              savedFilterId &&
              hasScrolledSearchContainer() &&
              result.nextCursor &&
              "timestamp" in result.nextCursor
            ) {
              const newResumeTs = (result.nextCursor as TimeCursor).timestamp;
              updateFilterPosition(savedFilterId, newResumeTs);
              const currentFilters = useFiltersStore.getState().savedFilters;
              if (currentFilters.length > 0) {
                useFiltersStore
                  .getState()
                  .actions.setSavedFilters(
                    currentFilters.map((f) =>
                      f.id === savedFilterId ? { ...f, resume_from: newResumeTs } : f
                    )
                  );
              }
            }

            return true;
          } catch (error) {
            logError(error as Error, { context: "loadMoreItems" });
            const failureCount = get().retryCount + 1;
            set({
              retryCount: failureCount,
              shouldRetry: failureCount < MAX_AUTO_RETRIES,
              nextRetryAt: Date.now() + retryBackoffMs(failureCount),
            });
            return false;
          } finally {
            loadMoreRequestInFlight = false;
            set({ loading: false });
          }
        } else {
          loadMoreRequestInFlight = false;
          set({ loading: false, loadMore: false });
          return false;
        }
      },

      /**
       * Gets total item count and price range for the current filters.
       * Uses cached defaultTotal when filters are at default state.
       * Skips refetch if only price filter changed (reduces jumping on slider).
       *
       * @param params - Search parameters for total query
       */
      getTotalItems: async (params: {
        exchangeRates: Currency;
        activeFilters: ActiveFilters;
        searchText: string;
        savedFilterId?: number;
        defaultTotal?: DefaultTotal;
      }) => {
        const { exchangeRates, activeFilters, searchText, savedFilterId, defaultTotal } = params;

        const { lastFiltersForTotal, lastSearchTextForTotal } = get();

        const shouldUseDefaultStats =
          !!defaultTotal && isDefaultFilters(activeFilters, searchText, savedFilterId);

        // Apply cached defaults immediately, then replace the total when exact count resolves.
        if (shouldUseDefaultStats) {
          set({
            itemTotal: defaultTotal.total,
            itemMinPrice: defaultTotal.minPrice,
            itemMaxPrice: defaultTotal.maxPrice,
            itemAvgPrice: defaultTotal.avgPrice,
            totalLoading: false,
            lastFiltersForTotal: activeFilters,
            lastSearchTextForTotal: searchText,
          });
        }

        // Skip total refetch if only price filter changed (reduces jumping on slider)
        if (
          !shouldUseDefaultStats &&
          lastFiltersForTotal &&
          searchText === lastSearchTextForTotal &&
          isEqual(filtersExcludingPrice(activeFilters), filtersExcludingPrice(lastFiltersForTotal))
        ) {
          return;
        }

        set({ totalLoading: true });

        const countRequestId = ++latestCountRequestId;
        const countOptions: LoadItemsOptions = {
          query: activeFilters,
          searchText,
          limit: 1,
          offset: 0,
          exchangeRates,
          filterid: savedFilterId,
          countOnly: true,
        };
        const countRequestKey = getCountRequestKey(countOptions);
        void loadItems(countOptions).then((countResult) => {
          if (countRequestId !== latestCountRequestId) {
            return;
          }
          if (!countResult) {
            set({ totalLoading: false });
            return;
          }
          lastResolvedCountKey = countRequestKey;
          set({
            itemTotal: countResult.total ?? 0,
            totalLoading: false,
            lastFiltersForTotal: activeFilters,
            lastSearchTextForTotal: searchText,
          });
        });

        if (!shouldUseDefaultStats) {
          const statsResult = await loadItems({
            query: activeFilters,
            searchText,
            limit: 1,
            offset: 0,
            exchangeRates,
            filterid: savedFilterId,
            totalOnly: true,
          });

          set({
            itemMinPrice: statsResult?.minPrice,
            itemMaxPrice: statsResult?.maxPrice,
            itemAvgPrice: statsResult?.avgPrice,
            lastFiltersForTotal: activeFilters,
            lastSearchTextForTotal: searchText,
          });
        }
      },

      /** Sets the main loading state */
      setLoading: (loading: boolean) => set({ loading }),

      /** Sets the current items array */
      setItems: (items: SearchResultItem[]) => set({ items }),

      /** Sets the total item count */
      setItemTotal: (total: number) => set({ itemTotal: total }),

      /** Sets the minimum price */
      setItemMinPrice: (price: number) => set({ itemMinPrice: price }),

      /** Sets the maximum price */
      setItemMaxPrice: (price: number) => set({ itemMaxPrice: price }),

      /** Sets the average price */
      setItemAvgPrice: (price: number | undefined) => set({ itemAvgPrice: price }),

      /** Sets the current offset for pagination */
      setItemOffset: (offset: number) => set({ itemOffset: offset }),

      /** Sets whether all items have been loaded */
      setHasReachedEnd: (ended: boolean) => set({ hasReachedEnd: ended }),

      /** Sets whether more items are available */
      setLoadMore: (loadMore: boolean) => set({ loadMore }),

      /** Sets whether no results were found */
      setNoResults: (noResults: boolean) => set({ noResults }),

      /** Sets the retry count for failed requests */
      setRetryCount: (count: number) => set({ retryCount: count }),

      /** Sets whether a retry should be attempted */
      setShouldRetry: (retry: boolean) => set({ shouldRetry: retry }),

      /** Sets the timestamp of the last load operation */
      setLastLoadTime: (time: number) => set({ lastLoadTime: time }),

      /** Sets whether to show the sign-in prompt */
      setShowSignInPrompt: (show: boolean) => set({ showSignInPrompt: show }),

      /** Resets the store to initial state */
      reset: () => {
        lastResolvedCountKey = null;
        set(initialState);
      },
    };

    return { actions };
  })
);
